/**
 * HW Notify Me — opens waitlist dialog and creates Klaviyo back-in-stock
 * subscriptions via the Client API.
 *
 * Endpoint:
 * POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=...
 *
 * @see https://developers.klaviyo.com/en/docs/how_to_set_up_custom_back_in_stock
 */

const KLAVIYO_BIS_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2024-10-15';
const STORAGE_PREFIX = 'hw-notify-me:';

/**
 * @param {string} email
 * @returns {boolean}
 */
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * @param {string} email
 * @param {string} variantId
 * @returns {string}
 */
function storageKey(email, variantId) {
  return `${STORAGE_PREFIX}${variantId}:${email.toLowerCase()}`;
}

/**
 * @param {string} email
 * @param {string} variantId
 * @returns {boolean}
 */
function wasAlreadySubscribed(email, variantId) {
  try {
    return localStorage.getItem(storageKey(email, variantId)) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * @param {string} email
 * @param {string} variantId
 */
function markSubscribed(email, variantId) {
  try {
    localStorage.setItem(storageKey(email, variantId), '1');
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {HTMLElement} root
 * @returns {{
 *   dialog: HTMLDialogElement,
 *   form: HTMLFormElement,
 *   emailInput: HTMLInputElement,
 *   submitBtn: HTMLButtonElement,
 *   statusEl: HTMLElement,
 *   imageEl: HTMLImageElement | null,
 *   imageWrap: HTMLElement | null,
 *   closeBtn: HTMLElement | null,
 * }}
 */
function getRefs(root) {
  const dialog = /** @type {HTMLDialogElement | null} */ (
    root.querySelector('[data-hw-notify-me-dialog]')
  );
  const form = /** @type {HTMLFormElement | null} */ (
    root.querySelector('[data-hw-notify-me-form]')
  );
  const emailInput = /** @type {HTMLInputElement | null} */ (
    root.querySelector('[data-hw-notify-me-email]')
  );
  const submitBtn = /** @type {HTMLButtonElement | null} */ (
    root.querySelector('[data-hw-notify-me-submit]')
  );
  const statusEl = /** @type {HTMLElement | null} */ (
    root.querySelector('[data-hw-notify-me-status]')
  );
  const imageEl = /** @type {HTMLImageElement | null} */ (
    root.querySelector('[data-hw-notify-me-image]')
  );
  const imageWrap = /** @type {HTMLElement | null} */ (
    root.querySelector('[data-hw-notify-me-image-wrap]')
  );
  const closeBtn = /** @type {HTMLElement | null} */ (
    root.querySelector('[data-hw-notify-me-close]')
  );

  if (!(dialog instanceof HTMLDialogElement) || !form || !emailInput || !submitBtn || !statusEl) {
    throw new Error('hw-notify-me: missing required dialog elements');
  }

  return {
    dialog,
    form,
    emailInput,
    submitBtn,
    statusEl,
    imageEl,
    imageWrap,
    closeBtn,
  };
}

/**
 * @param {HTMLElement} root
 */
function initNotifyMe(root) {
  if (root.dataset.hwNotifyMeInit === '1') return;
  root.dataset.hwNotifyMeInit = '1';

  let refs;
  try {
    refs = getRefs(root);
  } catch (_) {
    return;
  }

  const {
    dialog,
    form,
    emailInput,
    submitBtn,
    statusEl,
    imageEl,
    imageWrap,
    closeBtn,
  } = refs;

  const copy = {
    submit: root.dataset.copySubmit || "Notify Me When It's Back",
    submitting: root.dataset.copySubmitting || 'Submitting...',
    success: root.dataset.copySuccess || "You're on the list",
    already: root.dataset.copyAlready || "You're already on the waitlist",
    invalid: root.dataset.copyInvalid || 'Enter a valid email address',
    error: root.dataset.copyError || 'Something went wrong. Please try again.',
    missingConfig:
      root.dataset.copyMissingConfig ||
      'Notifications are unavailable right now. Please try again later.',
  };

  let previousScrollY = 0;
  /** @type {string} */
  let activeVariantId = root.dataset.variantId || '';

  /**
   * @param {string} message
   * @param {'error' | 'success' | ''} [tone]
   */
  function setStatus(message, tone = '') {
    statusEl.textContent = message;
    if (tone) {
      statusEl.dataset.tone = tone;
    } else {
      delete statusEl.dataset.tone;
    }
  }

  /**
   * @param {'idle' | 'loading' | 'success' | 'already'} state
   * @param {string} [label]
   */
  function setSubmitState(state, label) {
    submitBtn.dataset.state = state;
    submitBtn.disabled = state === 'loading' || state === 'success' || state === 'already';
    submitBtn.textContent = label || copy.submit;
  }

  function resetFormState() {
    setStatus('');
    setSubmitState('idle', copy.submit);
    emailInput.removeAttribute('aria-invalid');
    emailInput.value = '';
  }

  /**
   * @param {HTMLElement} trigger
   */
  function applyProductContext(trigger) {
    const variantId = trigger.dataset.hwNotifyVariantId || root.dataset.variantId || '';
    const imageUrl = trigger.dataset.hwNotifyImageUrl || root.dataset.imageUrl || '';
    const imageAlt = trigger.dataset.hwNotifyImageAlt || root.dataset.imageAlt || '';

    activeVariantId = variantId;
    root.dataset.variantId = variantId;

    if (imageWrap && imageEl) {
      if (imageUrl) {
        imageEl.src = imageUrl;
        imageEl.alt = imageAlt;
        imageWrap.hidden = false;
      } else {
        imageEl.removeAttribute('src');
        imageEl.alt = '';
        imageWrap.hidden = true;
      }
    }
  }

  function openDialog() {
    if (dialog.open) return;
    previousScrollY = window.scrollY;
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${previousScrollY}px`;
      dialog.showModal();
      emailInput.focus();
    });
  }

  function closeDialog() {
    if (!dialog.open) return;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: previousScrollY, behavior: 'instant' });
    dialog.classList.add('dialog-closing');
    dialog.addEventListener(
      'animationend',
      () => {
        dialog.classList.remove('dialog-closing');
        dialog.close();
        resetFormState();
      },
      { once: true },
    );
  }

  /**
   * @param {string} email
   * @param {string} variantId
   * @param {string} companyId
   * @returns {Promise<{ ok: boolean, already?: boolean, invalidEmail?: boolean, missingConfig?: boolean }>}
   */
  async function subscribe(email, variantId, companyId) {
    if (!companyId || !variantId) {
      return { ok: false, missingConfig: true };
    }

    if (wasAlreadySubscribed(email, variantId)) {
      return { ok: true, already: true };
    }

    const catalogVariantId = `$shopify:::$default:::${variantId}`;
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
              },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: catalogVariantId,
            },
          },
        },
      },
    };

    const url = `${KLAVIYO_BIS_URL}?company_id=${encodeURIComponent(companyId)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 202 || response.ok) {
      markSubscribed(email, variantId);
      return { ok: true };
    }

    let detail = '';
    try {
      const body = await response.json();
      detail = body?.errors?.[0]?.detail || body?.errors?.[0]?.code || '';
    } catch (_) {
      /* ignore */
    }

    const detailLower = String(detail).toLowerCase();
    if (detailLower.includes('email')) {
      return { ok: false, invalidEmail: true };
    }

    if (
      detailLower.includes('already') ||
      detailLower.includes('duplicate') ||
      response.status === 409
    ) {
      markSubscribed(email, variantId);
      return { ok: true, already: true };
    }

    return { ok: false };
  }

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const trigger = target.closest('[data-hw-notify-me-open]');
    if (!(trigger instanceof HTMLElement)) return;

    const dialogId = trigger.getAttribute('data-hw-notify-me-open');
    if (!dialogId || dialogId !== dialog.id) return;

    event.preventDefault();
    applyProductContext(trigger);
    resetFormState();
    openDialog();
  });

  closeBtn?.addEventListener('click', () => {
    closeDialog();
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      closeDialog();
    }
  });

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog();
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const email = emailInput.value.trim();
    const companyId = root.dataset.companyId || '';
    const variantId = activeVariantId || root.dataset.variantId || '';

    if (!isValidEmail(email)) {
      emailInput.setAttribute('aria-invalid', 'true');
      setStatus(copy.invalid, 'error');
      emailInput.focus();
      return;
    }

    emailInput.removeAttribute('aria-invalid');
    setStatus('');
    setSubmitState('loading', copy.submitting);

    try {
      const result = await subscribe(email, variantId, companyId);

      if (result.missingConfig) {
        setSubmitState('idle', copy.submit);
        setStatus(copy.missingConfig, 'error');
        return;
      }

      if (result.invalidEmail) {
        emailInput.setAttribute('aria-invalid', 'true');
        setSubmitState('idle', copy.submit);
        setStatus(copy.invalid, 'error');
        emailInput.focus();
        return;
      }

      if (result.ok && result.already) {
        setSubmitState('already', copy.already);
        setStatus(copy.already, 'success');
        return;
      }

      if (result.ok) {
        setSubmitState('success', copy.success);
        setStatus(copy.success, 'success');
        return;
      }

      setSubmitState('idle', copy.submit);
      setStatus(copy.error, 'error');
    } catch (_) {
      setSubmitState('idle', copy.submit);
      setStatus(copy.error, 'error');
    }
  });
}

function runInit() {
  document.querySelectorAll('[data-hw-notify-me]:not([data-hw-notify-me-init])').forEach((el) => {
    initNotifyMe(/** @type {HTMLElement} */ (el));
  });
}

function observeRoots() {
  runInit();
  const observer = new MutationObserver((mutations) => {
    const hasRoot = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        const el = /** @type {Element} */ (node);
        return el.hasAttribute('data-hw-notify-me') || el.querySelector('[data-hw-notify-me]') !== null;
      }),
    );
    if (hasRoot) runInit();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

runInit();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeRoots);
} else {
  observeRoots();
}
